/* eslint-disable react/destructuring-assignment */
import { PlusCircleOutlined, DownOutlined } from "@ant-design/icons";
import { Badge, Button, Dropdown, Image, message, Modal, Popover, Tooltip } from "antd";
import React, { useContext, useMemo, useState, useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { isString } from "lodash";
import { LuLayoutDashboard } from "react-icons/lu";
import { CRM_TYPES } from "../../../constants/crmConstants";
import useBrowserExt from "../../../hooks/useBrowserExt";
import { useWindowSize } from "../../../hooks/personalizedHooks";
import DownIcon from "../../../icons/DownIcon";
import FilterIcon from "../../../icons/js/FilterIcon";
import NotesIcon from "../../../icons/js/LeadProfileIcons/NotesIcon";
import { TaskAddIcon } from "../../../icons/js/LeadProfileIcons/TaskListIcon";
import { changeMessageOpportunity, setSelectedLeadRedux } from "../../../redux/slices/leadSlice";
import { NotesDrawer } from "./NotesDrawer";
import { statusColor } from "./engagementHelpers";
import LeadFilterBreadcrumbs from "./leadFilterBreadcrumbs";
import TranslateIcon from "../../../icons/js/LeadProfileIcons/TranslateIcon";
import { TranslationSettings } from "./TranslationSettings";
import { TaskDrawer } from "./TaskDrawer";
import { checkAccessPermission } from "../../../library/helpers";
import { AccessPermissionAction } from "../../../library/constants";
import CustomButton from "../../commonComponents/button";
import {
  getOpportunityDisplayName,
  getDefaultOpportunity,
  formatOpportunityId,
  ALL_OPPORTUNITY_TYPES_ID,
} from "../../../helpers/opportunityHelper";
import useOpportunityStatus from "../../../hooks/useOpportunityStatus";
import TranslateLogoSvg from "../../../icons/svg/translate-logo.svg";
import { TimelineList } from "./opporotunityLayer/OpportunityTimeline";
import { GET_OPPORTUNITY_STATUS_NEW } from "../../../graphql/query";
import { UPDATE_LEAD_OPPORTUNITY } from "../../../graphql/mutation";
import "./opporotunityLayer/OpportunityTimeline/OpportunityTimeline.css";
import useCrmIntegration from "../../../hooks/useCrmIntegration";
import { UserContext } from "../../../amplify/authenticator-provider";
import {
  getRootOppTypeId,
  getUserAllowedRootOppTypeIds,
  isUserUnscoped,
} from "../../../library/oppTypeScoping";
import { ROOT_OPP_TYPES, getRootConfig } from "../../../library/oppTypeRootConfig";

const getFormattedEventId = item => {
  if (!item) return "";
  return formatOpportunityId(
    item.prefix || "EAI",
    String(item.eventId || item.id).padStart(4, "0")
  );
};

/**
 * Normalize the tree from GET_OPPORTUNITY_STATUS_NEW into a consistent shape:
 * [{ id, name, children: [{ id, name, children: [] }] }]
 *
 * Sorted by `order` ascending at every level so the timeline iteration matches
 * the logical status flow. Without this, a status that is logically AFTER the
 * current one can end up BEFORE it in array order — and if it was ever touched
 * in history (e.g. via back-and-forth transitions) it gets mis-classified as
 * "completed" (green) instead of "skipped" (orange) or "pending".
 */
const normalizeStatusTree = nodes => {
  const mapped = (nodes || []).map(node => ({
    id: String(node.key),
    name: node.name,
    isDefault: node.isDefault,
    order: Number.isFinite(node.order) ? node.order : 0,
    children: normalizeStatusTree(node.childs),
  }));
  return mapped.sort((a, b) => a.order - b.order);
};

const TopToolbarJsx = props => {
  const {
    selectedLead,
    messageFilters,
    setMessageFilters,
    opportunityList,
    // Unfiltered enriched opp list — used to build the nested
    // dropdown rows for opps under inactive root tabs (so they
    // render the full "Service OPP: ... (EAI 1234 / ...)" label
    // instead of the short fallback) AND to resolve the selected
    // opportunity through a cross-tab pick, where opportunityList
    // briefly doesn't contain the just-picked id yet.
    fullOpportunityList = [],
    activeOpp,
    setActiveOpp,
    handleOppFilter,
    activeOppName,
    renderBackToNormalModeButton,
    handleBackButtonClick,
    refetchLeadDetails,
    // Active root opp_type tab — used to pick the icon-chip color
    // (Sales blue / Service orange) and to prefix the "All" label with
    // the tab's root name (e.g. "All sales").
    selectedOppTypeId,
    // Setter that mirrors a click in the nested opp dropdown back onto
    // the right-side root tab strip — selecting "Service > Body Shop"
    // should also switch the tab to Service so the rest of the panel
    // (notes / tasks / appointments) re-scopes.
    setSelectedOppTypeId,
  } = props;
  const { user } = useContext(UserContext);
  const { isBrowserExt } = useBrowserExt();
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < 640;
  const dispatch = useDispatch();
  const crmIntegrationType = useCrmIntegration();
  const allowParentSelection = crmIntegrationType === CRM_TYPES.DEALERSOCKET;
  const { messageOpportunity } = useSelector(state => state.lead);
  const { getStatusPath, getRootStatusName } = useOpportunityStatus();
  const setSelectedLead = lead => {
    dispatch(setSelectedLeadRedux(lead));
  };
  const [leadNotesData, setleadNotesData] = useState({
    open: false,
    leadNote: null,
    selectedLead: null,
    mode: "",
  });
  const [leadTaskData, setleadTaskData] = useState({
    open: false,
    leadTask: null,
    selectedLead: null,
    mode: "",
  });
  const [shouldBounce, setShouldBounce] = useState(true);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(true);
  const [statusTree, setStatusTree] = useState([]);
  // Controlled state for the nested opp dropdown — AntD doesn't close
  // the menu on a SubMenu title click (it treats it as an expand
  // toggle), so we close it ourselves whenever the user picks any row.
  const [oppDropdownOpen, setOppDropdownOpen] = useState(false);

  useEffect(() => {
    if (renderBackToNormalModeButton) {
      const timer = setTimeout(() => {
        setShouldBounce(false);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [renderBackToNormalModeButton]);

  const settings = useMemo(() => {
    if (selectedLead?.location?.settings) {
      return isString(selectedLead?.location?.settings)
        ? JSON.parse(selectedLead?.location?.settings)
        : selectedLead?.location?.settings;
    }
    return null;
  }, [selectedLead?.location?.settings]);

  const showStatusTimeline = !!settings?.SKIP_OPPORTUNITY_STATUS_HISTORY_CREATION;

  // The user explicitly picked the top-level "All opportunity" option —
  // no opp and no root scoping at all. Distinct from the per-root "All
  // <root>" rows (ALL_OPPORTUNITIES_ID), which still scope by selectedOppTypeId.
  const isAllOpportunitySelected = messageOpportunity === ALL_OPPORTUNITY_TYPES_ID;

  // Resolve selected opportunity — always default to "All"
  const selectedOpportunity = useMemo(() => {
    // Don't try to resolve/clobber the sentinel against opportunityList —
    // it deliberately doesn't correspond to any opp or "All <root>" row.
    if (isAllOpportunitySelected) return null;
    const found = (opportunityList || []).find(
      item => String(item.id) === String(messageOpportunity)
    );
    // If user explicitly selected "All", respect that choice
    if (found && found.name === "All") {
      return found;
    }
    // Cross-tab pick window: the user just selected an opp under a
    // different root, so ``setSelectedOppTypeId`` has fired but the
    // parent ``opportunityList`` rebuild hasn't landed yet — the
    // tab-filtered list doesn't contain the new id. If the id IS
    // present in the full (unfiltered) opp list, the pick is real;
    // return it and DO NOT dispatch the "All" reset, otherwise the
    // selection would be clobbered every cross-tab click.
    if (!found && messageOpportunity && messageOpportunity !== "#DEFAULTOPPID") {
      const inFull = (fullOpportunityList || []).find(
        item => String(item.id) === String(messageOpportunity)
      );
      if (inFull) {
        return inFull;
      }
    }
    // On first load or if not found, default to "All"
    if (!found || messageOpportunity === "#DEFAULTOPPID") {
      const allItem = (opportunityList || []).find(item => item.name === "All");
      if (allItem) {
        dispatch(changeMessageOpportunity(allItem.id));
        return allItem;
      }
    }
    return found;
  }, [messageOpportunity, opportunityList, fullOpportunityList, isAllOpportunitySelected]);

  const isAllSelected = selectedOpportunity?.name === "All" || isAllOpportunitySelected;

  // Default opportunity — used when "All" is selected to drive status/timeline display
  const defaultOpportunity = useMemo(
    () => getDefaultOpportunity(selectedLead?.leadOpportunities),
    [selectedLead?.leadOpportunities]
  );

  // Full opportunity object from lead data (has statusHistory, opportunityTypeId, etc.)
  // When "All" is selected, fall back to the default opportunity for timeline display
  const fullOpportunity = useMemo(() => {
    if (isAllSelected) {
      return defaultOpportunity;
    }
    return (selectedLead?.leadOpportunities || []).find(
      item => +item.id === +selectedOpportunity?.id
    );
  }, [selectedLead, selectedOpportunity, isAllSelected, defaultOpportunity]);

  const allSelectedStatusId = defaultOpportunity?.opportunityStatus?.id;

  const opportunityStatusName = isAllSelected
    ? getRootStatusName(allSelectedStatusId)
    : fullOpportunity?.opportunityStatus?.name || selectedLead?.leadStatusType?.type;
  const opportunityStatusId = isAllSelected
    ? allSelectedStatusId
    : fullOpportunity?.opportunityStatus?.id;
  const statusHistory = fullOpportunity?.statusHistory || [];

  // ── Fetch opportunity type statuses for timeline ──
  const [getOpportunityStatus] = useLazyQuery(GET_OPPORTUNITY_STATUS_NEW, {
    fetchPolicy: "cache-first",
  });

  // Fetch status definitions for the current opportunity's type.
  // Tree depends only on opportunityTypeId, so switching between opps of the
  // same type keeps the cached tree (avoids empty-timeline flicker).
  useEffect(() => {
    const fetchStatuses = async () => {
      const typeId = fullOpportunity?.opportunityTypeId;
      if (!typeId) {
        setStatusTree([]);
        return;
      }

      const locationIds = sessionStorage
        ?.getItem("currentLocationId")
        ?.split(",")
        .map(id => +id);

      // Try: type itself first, then fallback to 1
      const tryFetch = async id => {
        try {
          const result = await getOpportunityStatus({
            variables: { opportunityTypeId: id, locationIds },
          });
          if (result.data?.getOpportunityStatusNew?.length > 0) {
            return normalizeStatusTree(result.data.getOpportunityStatusNew);
          }
        } catch {
          // Ignore and try next
        }
        return null;
      };

      const result = (await tryFetch(typeId)) || (await tryFetch(1));
      setStatusTree(result || []);
    };

    fetchStatuses();
  }, [fullOpportunity?.opportunityTypeId]);

  // ── Status update from timeline (mirrors opportunityLayer.handleStatusChangeWithConfirmation) ──
  const [updateLeadOpportunity] = useMutation(UPDATE_LEAD_OPPORTUNITY);

  // Flatten the nested status tree to a { id: name } map for confirmation dialog lookup
  const statusNameById = useMemo(() => {
    const map = {};
    const walk = nodes => {
      (nodes || []).forEach(node => {
        map[String(node.id)] = node.name;
        walk(node.children);
      });
    };
    walk(statusTree);
    return map;
  }, [statusTree]);

  const handleStatusChangeWithConfirmation = selectedId => {
    if (!selectedId) return;
    if (!fullOpportunity?.id) return;
    // No-op if the user clicked the already-current status
    if (String(selectedId) === String(opportunityStatusId)) return;

    const statusName = statusNameById[String(selectedId)] || "this status";

    Modal.confirm({
      title: "Confirm Status Change",
      content: `Are you sure you want to change the opportunity status to "${statusName}"?`,
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        try {
          const resp = await updateLeadOpportunity({
            variables: {
              leadOpportunityId: fullOpportunity.id,
              opportunityStatusId: selectedId,
            },
          });
          if (resp?.data?.updateLeadOpportunity?.statusCode === 200) {
            message.success(resp.data.updateLeadOpportunity.message);
            if (refetchLeadDetails) refetchLeadDetails();
          } else {
            message.error(resp?.data?.updateLeadOpportunity?.message || "Failed to update status.");
          }
        } catch (err) {
          message.error("An error occurred while updating status. Please try again.");
        }
      },
    });
  };

  const addTask = () =>
    setleadTaskData({
      ...leadTaskData,
      open: true,
      selectedLead,
      mode: "ADD",
    });
  const addNote = () =>
    setleadNotesData({ ...leadNotesData, open: true, selectedLead, mode: "ADD" });

  const canAddTask = checkAccessPermission(AccessPermissionAction.TASK_MANAGEMENT_ADD_EDIT);
  // Gates the timeline's status-change affordance: when the user lacks the
  // LEAD_OPPORTUNITY_MANAGEMENT_ADD_EDIT permission, the timeline renders as
  // read-only (chevrons + child dropdowns stop being clickable) — matches how
  // OpportunityStatusCascader disables itself via `canAddEditOpportunity`.
  const canAddEditOpportunity = checkAccessPermission(
    AccessPermissionAction.LEAD_OPPORTUNITY_MANAGEMENT_ADD_EDIT
  );

  const filterCount =
    (messageFilters?.campaignIds?.length || 0) +
    (activeOpp && activeOpp !== "#DEFAULTOPPID" ? 1 : 0);

  return (
    <div data-testid="opportunity-timeline">
      {/* Main toolbar row.
          ``min-w-0`` on the row + ``flex-shrink-0`` on the right-side
          action group below let the left chip's truncate actually
          take effect — without it the row tries to size to the chip's
          intrinsic content width and shoves the action icons off the
          visible area in the browser-extension panel. */}
      <div
        className={`sm:px-4 sm:pb-2 px-2 ${
          window.location.pathname.includes("browser-ext") ? "py-3" : ""
        } flex items-center justify-between min-w-0 w-full`}
      >
        {/* Left: opp-type icon chip + opp dropdown (label + caret),
            status colored line below. Replaces the old single-row
            "<dropdown> <status-tag>" layout. The icon chip color
            mirrors the active root tab on the right-side panel:
            Sales → blue tint, Service → orange tint. */}
        {(() => {
          // Build a root id → opp_type name lookup from the BE-supplied
          // ``Location.activeRootOppTypes`` (preferred — it covers
          // roots the lead has no opp under, e.g. a Customer Service
          // tab on a Sales-only lead) plus the lead's own opp graph
          // as a defensive fallback. Used to resolve human-readable
          // labels for unknown roots like "AutoSavvy Customer Service".
          const rootNameById = new Map();
          (selectedLead?.location?.activeRootOppTypes || []).forEach(t => {
            if (t?.id != null && t?.name) rootNameById.set(Number(t.id), t.name);
          });
          (selectedLead?.leadOpportunities || []).forEach(opp => {
            const ot = opp?.opportunityType;
            if (!ot) return;
            const rId = getRootOppTypeId(ot);
            if (!rId || rootNameById.has(Number(rId))) return;
            let cursor = ot;
            for (let i = 0; i < 5; i += 1) {
              if (!cursor) break;
              if (Number(cursor.id) === Number(rId)) {
                if (cursor.name) rootNameById.set(Number(rId), cursor.name);
                break;
              }
              cursor = cursor.parentType;
            }
          });

          // Resolve per-root visual tokens for the icon chip + "All …"
          // label by config — never by hard-coded id — so a new entry
          // in ROOT_OPP_TYPES (e.g. Parts) flows through with zero
          // changes here. Unknown roots fall through ``getRootConfig``
          // and pick up the BE-supplied name as their display label.
          const activeRoot = getRootConfig(
            selectedOppTypeId,
            rootNameById.get(Number(selectedOppTypeId))
          );
          const { Icon: OppTypeIcon, chipBg, stroke: chipStroke } = activeRoot;
          const labelText = isAllOpportunitySelected
            ? "All opportunities"
            : selectedOpportunity
            ? isAllSelected
              ? activeRoot.allLabel
              : showStatusTimeline
              ? getFormattedEventId(selectedOpportunity)
              : getOpportunityDisplayName(selectedOpportunity)
            : "No opportunities";

          // Build the nested menu from the lead's FULL opp list (not
          // the tab-filtered ``opportunityList`` — that one only carries
          // opps for the active tab). Group by root opp_type and render
          // a submenu per visible root.
          const leadOpps = selectedLead?.leadOpportunities || [];
          const userAllowed = getUserAllowedRootOppTypeIds(user);
          const unscoped = isUserUnscoped(user);

          // Bucket the lead's opps by their resolved root id.
          const oppsByRoot = new Map();
          leadOpps.forEach(opp => {
            const rootId = getRootOppTypeId(opp?.opportunityType);
            if (!rootId) return;
            if (!oppsByRoot.has(rootId)) oppsByRoot.set(rootId, []);
            oppsByRoot.get(rootId).push(opp);
          });

          // Determine which roots are visible. A root shows up only if
          // (a) the user is permitted for it OR they're unscoped AND
          // (b) the lead actually has opps under it. Iterate over the
          // LEAD'S actual root ids (not ROOT_OPP_TYPES) so tenant-
          // specific roots like AutoSavvy Customer Service surface.
          // Known roots from ROOT_OPP_TYPES render first (Sales before
          // Service), then unknown roots in ascending id order so the
          // strip stays deterministic.
          const knownIds = ROOT_OPP_TYPES.map(c => c.rootOppTypeId);
          const knownIdSet = new Set(knownIds);
          const leadRootIds = [...oppsByRoot.keys()].map(Number);
          const orderedRootIds = [
            ...knownIds.filter(id => oppsByRoot.has(id)),
            ...leadRootIds.filter(id => !knownIdSet.has(id)).sort((a, b) => a - b),
          ];
          const visibleRoots = orderedRootIds
            .filter(id => {
              const rootOpps = oppsByRoot.get(id);
              if (!rootOpps || rootOpps.length === 0) return false;
              if (!unscoped && !userAllowed.has(id)) return false;
              return true;
            })
            .map(id => getRootConfig(id, rootNameById.get(Number(id))));

          // Skip the root-grouping submenu entirely when only one root
          // is visible (single permission OR lead with opps under just
          // one root) — flat list of "All <root>" + opps reads cleaner
          // than a single submenu wrapper around the same items.
          const flattenSingleRoot = visibleRoots.length === 1;

          // Look up enriched fields (prefix/eventId/opportunityStatus)
          // from the UNFILTERED list — when the user is on a different
          // tab, opportunityList doesn't contain rows from inactive
          // roots, so the cross-tab labels would otherwise fall back
          // to the short ``opp.name``. Falls back to opportunityList
          // for compatibility when fullOpportunityList isn't supplied.
          const enrichedSource =
            fullOpportunityList && fullOpportunityList.length > 0
              ? fullOpportunityList
              : opportunityList || [];
          const opportunityListById = new Map(enrichedSource.map(o => [String(o.id), o]));

          // Flat lookup keyed by menu-item key — the click handler
          // walks this instead of recursing into ``children`` so adding
          // new structure later doesn't need handler changes.
          const itemPayloadByKey = new Map();

          // Top-level "All opportunity" row — clears BOTH the opp pin and
          // the active root tab, so messagesV2 fetches across every
          // opportunity on the lead with no scoping at all. Distinct from
          // each root's "All <root>" row, which stays scoped to that root.
          const ALL_OPPORTUNITY_KEY = "all-opportunity";
          itemPayloadByKey.set(ALL_OPPORTUNITY_KEY, {
            oppId: ALL_OPPORTUNITY_TYPES_ID,
            rootId: null,
          });
          const allOpportunityMenuItem = {
            key: ALL_OPPORTUNITY_KEY,
            label: (
              <span className="flex items-center gap-2">
                <span
                  className="flex items-center justify-center"
                  style={{ width: 18, height: 18, background: "#F1F5F9", borderRadius: 4 }}
                >
                  <LuLayoutDashboard size={12} color="#64748B" />
                </span>
                <span className="text-sm" style={{ color: "rgba(0, 0, 0, 0.88)" }}>
                  All opportunities
                </span>
                <div className="m-0 text-xs leading-4 py-[1px] px-[4px] !rounded-[4px] flex-shrink-0 bg-[#E6F4FF]">
                  Read Only
                </div>
              </span>
            ),
          };

          // Build the opp-row items for a given root. Reused across the
          // flat-single-root branch and the multi-root submenu branch.
          const buildOppItemsForRoot = (cfg, sortedOpps) =>
            sortedOpps.map(opp => {
              const listItem = opportunityListById.get(String(opp.id));
              const childLabel = listItem
                ? showStatusTimeline
                  ? getFormattedEventId(listItem)
                  : getOpportunityDisplayName(listItem)
                : opp?.name || `Opportunity #${opp?.id}`;
              const childKey = `opp-${opp.id}`;
              itemPayloadByKey.set(childKey, {
                oppId: String(opp.id),
                rootId: cfg.rootOppTypeId,
              });
              return { key: childKey, label: childLabel };
            });

          const rootMenuItems = visibleRoots.reduce((acc, cfg) => {
            const rootOpps = oppsByRoot.get(cfg.rootOppTypeId);

            const sortedOpps = [...rootOpps].sort((a, b) => {
              const ta = new Date(a?.updatedDate || a?.createdDate || 0).getTime();
              const tb = new Date(b?.updatedDate || b?.createdDate || 0).getTime();
              return tb - ta;
            });

            const oppItems = buildOppItemsForRoot(cfg, sortedOpps);

            // "All <root>" payload — used by both branches. In the flat
            // branch it's a top-level row; in the nested branch it's
            // both the submenu header (via onTitleClick) and the first
            // child entry.
            const allKey = `all-${cfg.rootOppTypeId}`;
            itemPayloadByKey.set(allKey, {
              oppId: "#DEFAULTOPPID",
              rootId: cfg.rootOppTypeId,
            });

            const RootIcon = cfg.Icon;

            // Single-root flat branch: emit "All <root>" + opp rows
            // directly at the top level, no submenu wrapper.
            if (flattenSingleRoot) {
              acc.push({
                key: allKey,
                label: (
                  <span className="flex items-center gap-2">
                    <span
                      className="flex items-center justify-center"
                      style={{ width: 18, height: 18, background: cfg.chipBg, borderRadius: 4 }}
                    >
                      <RootIcon size={12} color={cfg.stroke} strokeWidth={1.5} />
                    </span>
                    <span className="text-sm" style={{ color: "rgba(0, 0, 0, 0.88)" }}>
                      {cfg.allLabel}
                    </span>
                    <span className="text-xs" style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                      ({sortedOpps.length})
                    </span>
                  </span>
                ),
              });
              oppItems.forEach(item => acc.push(item));
              return acc;
            }

            // Multi-root nested branch (unchanged behavior).
            const rootKey = `root-${cfg.rootOppTypeId}`;
            // Root title-click mirrors the "All <root>" payload so a
            // click on the header switches the tab without expanding.
            itemPayloadByKey.set(rootKey, {
              oppId: "#DEFAULTOPPID",
              rootId: cfg.rootOppTypeId,
            });

            acc.push({
              key: rootKey,
              // onTitleClick fires when the user clicks the submenu's
              // header row itself (not its children). AntD's Menu emits
              // ``{ key, domEvent }`` here — we ignore the event and
              // route through the shared handler so both root and child
              // clicks commit through one code path.
              onTitleClick: ({ key }) => {
                const payload = itemPayloadByKey.get(key);
                if (!payload) return;
                dispatch(changeMessageOpportunity(payload.oppId));
                if (typeof setSelectedOppTypeId === "function") {
                  setSelectedOppTypeId(payload.rootId);
                }
                setOppDropdownOpen(false);
              },
              label: (
                <span className="flex items-center gap-2">
                  <span
                    className="flex items-center justify-center"
                    style={{ width: 18, height: 18, background: cfg.chipBg, borderRadius: 4 }}
                  >
                    <RootIcon size={12} color={cfg.stroke} strokeWidth={1.5} />
                  </span>
                  <span className="text-sm" style={{ color: "rgba(0, 0, 0, 0.88)" }}>
                    {cfg.displayName}
                  </span>
                  <span className="text-xs" style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                    ({sortedOpps.length})
                  </span>
                </span>
              ),
              children: [{ key: allKey, label: cfg.allLabel }, ...oppItems],
            });
            return acc;
          }, []);

          // Resolve the selected key so AntD highlights the current row.
          // "All opportunity" keys as ``all-opportunity``, "All <root>"
          // rows key as ``all-<rootId>``, individual opps as
          // ``opp-<oppId>`` — matches the structure above.
          const selectedKey = isAllOpportunitySelected
            ? ALL_OPPORTUNITY_KEY
            : isAllSelected
            ? `all-${activeRoot.rootOppTypeId}`
            : selectedOpportunity
            ? `opp-${selectedOpportunity.id}`
            : undefined;

          // Child-row clicks come through ``onClick``. Root-row clicks
          // come through each submenu's ``onTitleClick`` (set above).
          // Both routes resolve a payload from ``itemPayloadByKey`` and
          // commit activeOpp + active tab atomically.
          const handleMenuClick = info => {
            const key = info?.key;
            if (!key) return;
            const payload = itemPayloadByKey.get(key);
            if (!payload) return;
            dispatch(changeMessageOpportunity(payload.oppId));
            if (typeof setSelectedOppTypeId === "function") {
              setSelectedOppTypeId(payload.rootId);
            }
            setOppDropdownOpen(false);
          };

          const menuItemsWithAllOpportunity = [allOpportunityMenuItem, ...rootMenuItems];

          // Per-root opp counts for the "All opportunity" summary line
          // (e.g. "3 Sales • 2 Service • 1 Parts") — reuses the same
          // visibleRoots/oppsByRoot buckets the dropdown menu is built from.
          const oppCountSummary = visibleRoots
            .map(
              cfg =>
                `${oppsByRoot.get(cfg.rootOppTypeId)?.length || 0} ${
                  cfg.shortLabel || cfg.displayName
                }`
            )
            .join(" • ");

          return (
            <div className="flex flex-col items-start gap-1 min-w-0 flex-shrink-0">
              <Dropdown
                trigger={["click"]}
                open={oppDropdownOpen}
                onOpenChange={setOppDropdownOpen}
                menu={{
                  items: menuItemsWithAllOpportunity,
                  onClick: handleMenuClick,
                  selectedKeys: selectedKey ? [selectedKey] : [],
                }}
              >
                <span className="flex-row-centered gap-2 items-center cursor-pointer min-w-0">
                  <span
                    className="flex items-center justify-center"
                    style={{
                      width: 20,
                      height: 20,
                      background: isAllOpportunitySelected ? "#F1F5F9" : chipBg,
                      borderRadius: 4,
                    }}
                  >
                    {isAllOpportunitySelected ? (
                      <LuLayoutDashboard size={13} color="#64748B" />
                    ) : (
                      <OppTypeIcon size={13} color={chipStroke} strokeWidth={1.33} />
                    )}
                  </span>
                  <Tooltip title={labelText} placement="top">
                    <span
                      className="text-sm font-[600] truncate"
                      style={{
                        color: "rgba(0, 0, 0, 0.88)",
                        // Match the "All <root>" baseline width so a long
                        // specific-opp label (e.g. "Repair Order #99999
                        // (EAI 3189653) / Completed - RO Closed") clips
                        // with ellipsis instead of stretching the trigger
                        // and pushing the toolbar icons off-row. The
                        // browser-extension panel is narrower, so the
                        // cap is tighter there to keep the action icons
                        // inside the visible area.
                        maxWidth: isBrowserExt ? 110 : 140,
                      }}
                    >
                      {labelText}
                    </span>
                  </Tooltip>
                  {isAllOpportunitySelected && (
                    <div className="m-0 text-xs leading-4 py-[1px] px-[4px] !rounded-[4px] flex-shrink-0 bg-[#E6F4FF]">
                      Read Only
                    </div>
                  )}
                  <DownIcon className="flex-shrink-0" />
                </span>
              </Dropdown>
              {isAllOpportunitySelected
                ? oppCountSummary && (
                    <span
                      className="text-xs leading-4 font-semibold cursor-default text-black-65"
                      data-testid="opp-count-summary"
                    >
                      {oppCountSummary}
                    </span>
                  )
                : opportunityStatusName && (
                    <Tooltip title={getStatusPath(opportunityStatusId)}>
                      <span
                        className={`text-xs leading-4 font-semibold cursor-default ${statusColor(
                          opportunityStatusName,
                          "text"
                        )}`}
                        data-testid="status-tag"
                      >
                        {opportunityStatusName}
                      </span>
                    </Tooltip>
                  )}
            </div>
          );
        })()}

        {/* Center: Show/Hide status toggle */}
        <div className="flex items-center justify-center flex-1">
          {showStatusTimeline && (
            <button
              type="button"
              className={`show-status-btn ${isTimelineExpanded ? "expanded" : ""}`}
              onClick={() => setIsTimelineExpanded(prev => !prev)}
              data-testid="show-status-toggle"
            >
              {isTimelineExpanded ? "Hide status" : "Show status"}
              <DownOutlined />
            </button>
          )}
          {renderBackToNormalModeButton && (
            <CustomButton
              type="primary"
              tooltipTitle="This will take you back to normal chat view"
              ghost
              className={shouldBounce ? "!animate-bounce" : ""}
              btnText="View all messages"
              handleClick={() => handleBackButtonClick && handleBackButtonClick()}
            />
          )}
        </div>

        {/* Right: action buttons */}
        {isBrowserExt || isMobile ? (
          <div className="flex-row-centered gap-4 flex-shrink-0">
            <Dropdown
              menu={{
                items: [
                  {
                    label: (
                      <span onClick={addTask} className="flex-row-centered gap-1">
                        <TaskAddIcon /> Add task
                      </span>
                    ),
                    disabled: !canAddTask,
                  },
                  {
                    label: (
                      <span onClick={addNote} className="flex-row-centered gap-1">
                        <NotesIcon /> Add Note
                      </span>
                    ),
                  },
                ],
              }}
            >
              <PlusCircleOutlined className="text-grey-500 cursor-pointer text-xl" />
            </Dropdown>
            <Popover
              open={props.visibleTranslationSettings}
              onOpenChange={() => {
                props.setVisibleTranslationSetting(!props.visibleTranslationSettings);
              }}
              trigger="click"
              placement="bottomRight"
              overlayStyle={{ maxWidth: "calc(100vw - 16px)" }}
              content={<TranslationSettings {...props} />}
            >
              <TranslateIcon
                className="cursor-pointer"
                onClick={() =>
                  props.setVisibleTranslationSetting(!props.visibleTranslationSettings)
                }
              />
            </Popover>
            <Popover
              trigger="click"
              content={
                <LeadFilterBreadcrumbs
                  filters={messageFilters}
                  setFilters={payload => {
                    setMessageFilters({ ...payload, page: 1 });
                  }}
                  opportunityList={opportunityList}
                  activeOpp={activeOpp}
                  setActiveOpp={setActiveOpp}
                  handleOppFilter={handleOppFilter}
                  activeOppName={activeOppName}
                  isFilterBreadcrumbs
                />
              }
            >
              <Badge count={filterCount}>
                <FilterIcon className="cursor-pointer" />
              </Badge>
            </Popover>
          </div>
        ) : (
          <div className="flex-row-centered gap-1 items-center flex-shrink-0">
            {/* Icon-only Add Task button */}
            <Tooltip title="Add Task">
              <Button
                onClick={addTask}
                disabled={!canAddTask}
                className="flex-row-centered items-center justify-center"
                icon={<TaskAddIcon />}
                aria-label="Add Task"
                data-testid="action-add-task"
              />
            </Tooltip>
            {/* Icon-only Add Note button */}
            <Tooltip title="Add Note">
              <Button
                onClick={addNote}
                className="flex-row-centered items-center justify-center"
                icon={<NotesIcon />}
                aria-label="Add Note"
                data-testid="action-add-note"
              />
            </Tooltip>
            <Popover
              open={props.visibleTranslationSettings}
              onOpenChange={() => {
                props.setVisibleTranslationSetting(!props.visibleTranslationSettings);
              }}
              trigger="click"
              content={
                <TranslationSettings
                  page={props.page}
                  setVisibleTranslationSetting={props.setVisibleTranslationSetting}
                  targetLangForCustomer={props.targetLangForCustomer}
                  setTargetLangForCustomer={props.setTargetLangForCustomer}
                  updateTranslationSetting={props.updateTranslationSetting}
                  targetLangForEmployee={props.targetLangForEmployee}
                  isAutoTranslate={props.isAutoTranslate}
                  isIncludeOriginalMessage={props.isIncludeOriginalMessage}
                  isTranslatePreviewShow={props.isTranslatePreviewShow}
                  selectedLead={props.selectedLead}
                  languages={props.languages}
                  setTargetLangForEmployee={props.setTargetLangForEmployee}
                  setIsAutoTranslate={props.setIsAutoTranslate}
                  setIsIncludeOriginalMessage={props.setIsIncludeOriginalMessage}
                  setIsTranslatePreviewShow={props.setIsTranslatePreviewShow}
                />
              }
            >
              <Button
                icon={<Image src={TranslateLogoSvg} preview={false} />}
                className="flex-row-centered gap-1 px-1.5 items-center"
              />
            </Popover>
            <Popover
              trigger="click"
              content={
                <LeadFilterBreadcrumbs
                  filters={messageFilters}
                  setFilters={payload => {
                    setMessageFilters({ ...payload, page: 1 });
                  }}
                  opportunityList={opportunityList}
                  activeOpp={activeOpp}
                  setActiveOpp={setActiveOpp}
                  handleOppFilter={handleOppFilter}
                  activeOppName={activeOppName}
                  isFilterBreadcrumbs
                />
              }
            >
              <Button className="flex-row-centered gap-1 px-1.5 items-center">
                <Badge count={filterCount}>
                  <FilterIcon />
                </Badge>
              </Button>
            </Popover>
          </div>
        )}
      </div>

      {/* Expandable timeline section — when "All" is selected, shows default opportunity's timeline */}
      {showStatusTimeline && (
        <div className={`timeline-expand-wrapper ${isTimelineExpanded ? "expanded" : ""}`}>
          {isTimelineExpanded && (
            <TimelineList
              statusTree={statusTree}
              statusHistory={statusHistory}
              currentStatusId={opportunityStatusId}
              onStatusChange={
                canAddEditOpportunity ? handleStatusChangeWithConfirmation : undefined
              }
              allowParentSelection={allowParentSelection}
            />
          )}
        </div>
      )}

      <NotesDrawer
        setSelectedLead={setSelectedLead}
        leadNotesData={leadNotesData}
        setleadNotesData={setleadNotesData}
        selectedOppTypeId={selectedOppTypeId}
      />
      <TaskDrawer
        setSelectedLead={setSelectedLead}
        leadTaskData={leadTaskData}
        setleadTaskData={setleadTaskData}
        selectedOppTypeId={selectedOppTypeId}
      />
    </div>
  );
};

export default TopToolbarJsx;
